import { AxiosError } from "axios";
import { ADMIN_API_ENDPOINTS } from "@/config/apiEndpoints/adminUrls";
import { handleApiError } from "@/lib/utils";
import adminAxios from "@/lib/axios";

export interface IElectiveCategory {
  id?: number;
  elective_id?: number;
  name: string;
  name_text_color?: string;
  order_index: number;
  status?: boolean;
  is_active?: boolean;
  semesters?: IElectiveSemester[];
}

export interface IElectiveSemester {
  id?: number;
  category_id: number;
  name: string;
  order_index: number;
  status?: boolean;
  is_active?: boolean;
  subjects?: IElectiveSubject[];
}

export interface IElectiveSubject {
  id?: number;
  semester_id: number;
  subject_name: string;
  subject_name_text_color?: string;
  credits: number;
  credits_text_color?: string;
  priority: number;
  status?: boolean;
  is_active?: boolean;
}

export interface IElective {
  id?: number;
  page_id?: number;
  component_id?: number;
  title?: string;
  title_text_color?: string;
  sub_title?: string;
  sub_title_text_color?: string;
  variant?: string;
  status?: boolean;
  categories?: IElectiveCategory[];
}

// Category API methods
export async function createElectiveCategory(payload: Partial<IElectiveCategory>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.ELECTIVE.CATEGORY.CREATE,
      payload
    );

    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateElectiveCategory(id: number, payload: Partial<IElectiveCategory>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.ELECTIVE.CATEGORY.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteElectiveCategory(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.ELECTIVE.CATEGORY.DELETE(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getElectiveCategoriesByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.ELECTIVE.CATEGORY.GET_BY_COMPONENT_ID(componentId)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// Semester API methods
export async function createElectiveSemester(payload: Partial<IElectiveSemester>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.ELECTIVE.SEMESTER.CREATE,
      payload
    );

    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateElectiveSemester(id: number, payload: Partial<IElectiveSemester>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.ELECTIVE.SEMESTER.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteElectiveSemester(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.ELECTIVE.SEMESTER.DELETE(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getElectiveSemestersByCategoryId(categoryId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.ELECTIVE.SEMESTER.GET_BY_CATEGORY_ID(categoryId)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// Subject API methods
export async function createElectiveSubject(payload: Partial<IElectiveSubject>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.ELECTIVE.SUBJECT.CREATE,
      payload
    );

    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateElectiveSubject(id: number, payload: Partial<IElectiveSubject>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.ELECTIVE.SUBJECT.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteElectiveSubject(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.ELECTIVE.SUBJECT.DELETE(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getElectiveSubjectsBySemesterId(semesterId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.ELECTIVE.SUBJECT.GET_BY_SEMESTER_ID(semesterId)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// Elective API methods
export async function createElective(payload: Partial<IElective>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.ELECTIVE.CREATE,
      payload
    );

    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateElective(id: number, payload: Partial<IElective>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.ELECTIVE.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getElectiveById(id: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.ELECTIVE.GET_BY_ID(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getElectiveByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.ELECTIVE.GET_BY_COMPONENT_ID(componentId)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteElective(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.ELECTIVE.DELETE(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function cloneElectiveWithCategories(
  sourceElectiveId: number,
  targetPageComponentId: number,
  targetElectiveId?: number | null
) {
  try {
    const response = await adminAxios.post(ADMIN_API_ENDPOINTS.ELECTIVE.CLONE,
      {
        source_elective_id: sourceElectiveId,
        target_page_component_id: targetPageComponentId,
        target_elective_id: targetElectiveId || null,
      }
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

